export type SlideoutMomentNavigationFilters = {
  searchFilterActive: boolean;
  categoriesFilter?: { selectedCats?: unknown[] };
  productGroupsFilter?: { selectedGroups?: unknown[] };
  audiencesFilter?: { selectedAudiences?: unknown[] };
  selectedEventsFilter?: { selectedEvents?: unknown[] };
  selectedFeaturesFilter?: { selectedFeatures?: unknown[] };
  selectedProductsFilter?: { selectedProducts?: unknown[] };
  lanesTiersFilter?: { selectedLanesTiers?: unknown[] };
  statusFilter?: { selectedStatus?: unknown[] };
  genAiFilter?: { selectedFilters?: unknown[] };
  revenueImpactFilter?: { selectedFilters?: unknown[] };
  momentTypeFilterState?: { selectedMomentTypes?: unknown[] };
  mustWinDropdownSelection?: unknown[] | unknown;
};

export type SlideoutMomentNavigationParams = {
  releases?: { releaseScheduled?: unknown[] };
  filteredReleases?: unknown[];
  filters: SlideoutMomentNavigationFilters;
  tierOptions: string[];
  getCurrentTier: () => string;
  cardTierFallback?: string;
  cardGeneralInfoTierFallback?: string;
};

const normalizeTier = (tier?: string) =>
  tier ? tier.toString().trim().toUpperCase() : '';

const hasActiveNavigationFilters = (
  filters: SlideoutMomentNavigationFilters
): boolean => {
  return (
    filters.searchFilterActive ||
    (filters.categoriesFilter?.selectedCats?.length ?? 0) > 0 ||
    (filters.productGroupsFilter?.selectedGroups?.length ?? 0) > 0 ||
    (filters.audiencesFilter?.selectedAudiences?.length ?? 0) > 0 ||
    (filters.selectedEventsFilter?.selectedEvents?.length ?? 0) > 0 ||
    (filters.selectedFeaturesFilter?.selectedFeatures?.length ?? 0) > 0 ||
    (filters.selectedProductsFilter?.selectedProducts?.length ?? 0) > 0 ||
    (filters.lanesTiersFilter?.selectedLanesTiers?.length ?? 0) > 0 ||
    (filters.statusFilter?.selectedStatus?.length ?? 0) > 0 ||
    (filters.genAiFilter?.selectedFilters?.length ?? 0) > 0 ||
    (filters.revenueImpactFilter?.selectedFilters?.length ?? 0) > 0 ||
    (filters.momentTypeFilterState?.selectedMomentTypes?.length ?? 0) > 0 ||
    (Array.isArray(filters.mustWinDropdownSelection)
      ? filters.mustWinDropdownSelection.length > 0
      : Boolean(filters.mustWinDropdownSelection))
  );
};

const isNavigableRelease = (release: any) => {
  const type = (release?.type || '').toString().toLowerCase();
  if (!type) return true;
  if (type === 'key dates') return false;
  if (type.includes('events')) return false;
  return true;
};

/**
 * Extracts all moment IDs from roadmap data in navigation order.
 * Extracted from SlideOutPanel/index.tsx (Part 2A modularisation).
 */
export function getAllSlideoutMomentIds(
  params: SlideoutMomentNavigationParams
): string[] {
  const momentIds: string[] = [];
  const seenIds = new Set<string>();

  const hasActiveFilters = hasActiveNavigationFilters(params.filters);

  const releaseList: any[] = hasActiveFilters
    ? params.filteredReleases || []
    : params.releases?.releaseScheduled || [];

  if (!Array.isArray(releaseList) || releaseList.length === 0) {
    return momentIds;
  }

  const currentTier = normalizeTier(
    params.getCurrentTier() ||
      params.cardGeneralInfoTierFallback ||
      params.cardTierFallback
  );

  const tierOrder =
    params.tierOptions && params.tierOptions.length > 0
      ? params.tierOptions.map(t => t.toString().trim())
      : ['L1', 'L2', 'L3', 'Other'];
  const normalizedTierOrder = tierOrder.map(normalizeTier).filter(Boolean);

  const orderedTiers = currentTier
    ? [currentTier, ...normalizedTierOrder.filter(tier => tier !== currentTier)]
    : normalizedTierOrder;

  const orderedMoments: { id: string; tier?: string }[] = [];

  releaseList.filter(isNavigableRelease).forEach((release: any) => {
    if (release.projects && Array.isArray(release.projects)) {
      release.projects.forEach((timeSlot: any) => {
        if (timeSlot.project && Array.isArray(timeSlot.project)) {
          timeSlot.project.forEach((moment: any) => {
            if (moment.issueId && !seenIds.has(moment.issueId)) {
              orderedMoments.push({
                id: moment.issueId,
                tier:
                  moment?.generalInfo?.tier ||
                  moment?.tier ||
                  moment?.generalInfo?.tierName ||
                  moment?.generalInfo?.tierValue
              });
              seenIds.add(moment.issueId);
            }
          });
        }
      });
    }
  });

  const finalIds: string[] = [];
  const used = new Set<string>();

  orderedTiers.forEach(tier => {
    orderedMoments.forEach(moment => {
      if (!used.has(moment.id) && normalizeTier(moment.tier) === tier) {
        finalIds.push(moment.id);
        used.add(moment.id);
      }
    });
  });

  orderedMoments.forEach(moment => {
    if (!used.has(moment.id)) {
      finalIds.push(moment.id);
      used.add(moment.id);
    }
  });

  return finalIds;
}

export function getNextSlideoutMomentId(
  currentIssueId: string,
  params: SlideoutMomentNavigationParams
): string | null {
  const allIds = getAllSlideoutMomentIds(params);
  const currentIndex = allIds.indexOf(currentIssueId);

  if (currentIndex === -1 || currentIndex === allIds.length - 1) {
    return null;
  }

  return allIds[currentIndex + 1];
}

export function getPreviousSlideoutMomentId(
  currentIssueId: string,
  params: SlideoutMomentNavigationParams
): string | null {
  const allIds = getAllSlideoutMomentIds(params);
  const currentIndex = allIds.indexOf(currentIssueId);

  if (currentIndex === -1 || currentIndex === 0) {
    return null;
  }

  return allIds[currentIndex - 1];
}
